import { apiClient } from './client';

/**
 * Every file the app uploads goes straight from the browser to storage, not
 * through the API:
 *
 *   1. `POST /uploads/direct` signs a PUT for this file's type and size.
 *   2. The browser PUTs the file to that URL (S3).
 *   3. The caller sends the returned `uploadKey` to the feature's endpoint,
 *      which checks the file and stores it.
 *
 * The purpose is fixed when the URL is signed, so a key signed for one
 * feature cannot be claimed by another. Limits mirror the API's
 * `upload-purposes.ts`; the API refuses anything outside them anyway.
 */
export type UploadPurpose =
  | 'clinic-logo'
  | 'landing-image'
  | 'profile-photo'
  | 'signature'
  | 'payment-proof'
  | 'expense-attachment'
  | 'purchase-attachment'
  | 'asset-attachment'
  | 'ticket-attachment'
  | 'chat-file'
  | 'payment-screenshot'
  | 'patient-file'
  | 'xray';

interface SignedUpload {
  uploadKey: string;
  uploadUrl: string;
  method: 'PUT';
  headers: Record<string, string>;
  expiresIn: number;
}

/**
 * The type to declare for a file. Browsers leave `type` empty for formats
 * they do not know (DICOM, some sensor exports); those go as a generic binary
 * and only the X-ray import accepts them.
 */
function declaredType(file: File): string {
  return file.type || 'application/octet-stream';
}

/** Uploads one file to storage and returns the key to hand to the API. */
export async function uploadDirect(
  file: File,
  purpose: UploadPurpose,
): Promise<string> {
  const { data: signed } = await apiClient.post<SignedUpload>(
    '/uploads/direct',
    {
      purpose,
      contentType: declaredType(file),
      size: file.size,
      fileName: file.name,
    },
  );

  // Not through apiClient: S3 must not get our auth header or cookies, and
  // the signed headers have to be sent exactly as given.
  const response = await fetch(signed.uploadUrl, {
    method: signed.method,
    headers: signed.headers,
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Upload failed (${response.status})`);
  }
  return signed.uploadKey;
}

/** `uploadDirect` for several files, in parallel. Keys keep the files' order. */
export function uploadDirectMany(
  files: File[],
  purpose: UploadPurpose,
): Promise<string[]> {
  return Promise.all(files.map((file) => uploadDirect(file, purpose)));
}
